// MCP / Agent tools tab: two rosters — MCP servers and built-in tools (with
// shell binaries promoted to their own rows) — each opening a full per-entity
// DETAIL PAGE routed at #/tools/<kind>/<name>.
//
// Enterprise adaptation: API calls thread the team/repo/user scope via
// scopeQs(), and the "Suggested fix" card is auto-loaded only for admins
// viewing org-wide data; everyone else sees an "Investigate & Suggest Fix"
// button that triggers on-demand LLM drafting.
import { state, $, esc, num, get, post, dayOf, scopeQs } from './core'
import { syncHash } from './router'
import { openDetail, startErrorWalk } from './sessions'
import {
  fact, pageTile, rateChart, sectHead, sourceLabel, sparkline, TIME_PRESETS,
  trendChart, trendGranLabel, wireTrendTooltip, windowPhrase,
} from './health-ui'
import { detailStillCurrent, filterRows, isToolSelected, rollupTail } from './tools-roster'
import { mountScopeBar, hasScopeControls } from './filterbar'

var tlReport = null;
var tlReportKey = null;
var tlStatus = '';
var tlSearch = '';
var tlShowAllShell = false;
var tlDetail = null;
var tlDetailKey = null;

var KINDS = [
  { k: 'mcp', l: 'MCP servers', noun: 'MCP', full: 'MCP servers the agent called' },
  { k: 'builtin', l: 'Built-in tools', noun: 'built-in', full: 'Built-in tools and the shell binaries they ran' }
];

var FLAGS = {
  'high-error': { label: 'High error', color: 'var(--red)', hero: true, tip: 'At least a fifth of its calls failed, over enough calls to be a pattern.' },
  degrading: { label: 'Degrading', color: 'var(--amber)', hero: true, tip: 'Its error rate is materially worse in the recent half of this window than the earlier half.' }
};
function hasFlag(r, f) { return (r.flags || []).indexOf(f) >= 0; }

function flagTip(r, f) {
  var fl = FLAGS[f];
  if (f === 'high-error' && r.calls) {
    var base = pct(r.errorCalls / r.calls) + ' of ' + num(r.calls) + ' calls failed';
    return r.dominantErrorCategory
      ? base + ', ' + (r.dominantErrorShare >= 0.5 ? 'mostly ' : 'most often ') + r.dominantErrorCategory + '.'
      : base + '.';
  }
  if (f === 'degrading' && r.recentErrorRate != null) {
    return 'Error rate went from ' + pct(r.priorErrorRate || 0) + ' to ' + pct(r.recentErrorRate) + ' across this window.';
  }
  return fl ? fl.tip : '';
}

function pct(x) { return Math.round((x || 0) * 100) + '%'; }

function tlWinQuery() {
  var q = (state.toolWin === 'custom' && state.toolFrom && state.toolTo)
    ? 'from=' + encodeURIComponent(new Date(state.toolFrom).toISOString()) +
      '&to=' + encodeURIComponent(new Date(state.toolTo + 'T23:59:59').toISOString())
    : 'days=' + encodeURIComponent(state.toolWin === 'all' ? 'all' : String(state.toolWin));
  if (state.toolSource) q += '&source=' + encodeURIComponent(state.toolSource);
  q += scopeQs();
  return q;
}

function winPhrase(presetPrefix, allTime) {
  return windowPhrase(tlReport && tlReport.windowDays, presetPrefix, allTime, state.toolFrom, state.toolTo);
}

function tlWinKey() {
  var win = state.toolWin === 'custom' ? 'custom:' + state.toolFrom + ':' + state.toolTo : String(state.toolWin);
  return win + '|' + (state.toolSource || '') + '|' + scopeQs();
}

export function getToolParams(): Record<string, string> {
  var q: Record<string, string> = {};
  if (state.toolKind && state.toolKind !== 'mcp') q.kind = state.toolKind;
  if (state.tool) q.name = state.tool;
  if (state.toolWin === 'custom') {
    if (state.toolFrom) q.from = state.toolFrom;
    if (state.toolTo) q.to = state.toolTo;
  } else if (state.toolWin === 'all') q.win = 'all';
  else if (state.toolWin !== 30) q.win = String(state.toolWin);
  if (state.toolSource) q.source = state.toolSource;
  if (state.tool && state.toolFilter) q.tool = state.toolFilter;
  return q;
}

function applyWinFromQuery(query: Record<string, string>) {
  var win = query && query.win;
  if (query && (query.from || query.to)) {
    state.toolWin = 'custom';
    state.toolFrom = query.from || '';
    state.toolTo = query.to || '';
  } else if (win === 'all') state.toolWin = 'all';
  else if (win === '7' || win === '14' || win === '90') state.toolWin = parseInt(win, 10);
  else state.toolWin = 30;
  state.toolSource = (query && query.source) || '';
  state.toolFilter = (query && query.tool) || '';
}

export function applyToolParams(query: Record<string, string>) {
  state.toolKind = query.kind === 'builtin' ? 'builtin' : 'mcp';
  state.tool = query.name || null;
  applyWinFromQuery(query);
  renderTools();
}

export function renderTools() {
  var box = $('#tool-health');
  if (!box) return;
  if (tlReport && tlReportKey === tlWinKey()) { paintTools(); return; }
  loadTools();
}

function loadTools() {
  var box = $('#tool-health');
  if (!box) return;
  var want = tlWinKey();
  box.innerHTML = '<div class="sk-empty">Loading tool health…</div>';
  get('/api/tool-health?' + tlWinQuery()).then(function (d) {
    if (tlWinKey() !== want) return;
    tlReport = d || { mcp: { rows: [] }, builtin: { rows: [] } };
    tlReportKey = want;
    paintTools();
  }).catch(function () {
    if (tlWinKey() !== want) return;
    box.innerHTML = '<div class="sk-empty">Could not load tool health.</div>';
  });
}

export function openTool(kind, name, query?) {
  if (query) applyWinFromQuery(query);
  var nextKind = kind === 'builtin' ? 'builtin' : 'mcp';
  if (nextKind !== state.toolKind) { tlStatus = ''; tlShowAllShell = false; }
  if (!query && name !== state.tool) state.toolFilter = '';
  state.toolKind = nextKind;
  state.tool = name || null;
  syncHash();
  if (state.tool) window.scrollTo(0, 0);
  if (!tlReport || tlReportKey !== tlWinKey()) loadTools();
  else paintTools();
}

function setToolWin(win) {
  state.toolWin = win;
  syncHash();
  if (win === 'custom' && (!state.toolFrom || !state.toolTo)) { paintTools(); return; }
  loadTools();
}

function setToolSource(source) {
  state.toolSource = source;
  state.tool = null;
  syncHash();
  loadTools();
}

function section(d) { return (d && d[state.toolKind]) || { rows: [] }; }

function paintTools() {
  var box = $('#tool-health');
  if (!box || !tlReport) return;
  var d = tlReport;

  var anyRows = (d.mcp && d.mcp.rows.length) || (d.builtin && d.builtin.rows.length);
  if (!anyRows) {
    box.innerHTML =
      '<div class="panel sk-panel"><div class="panel-head"><h2>MCP/Tools</h2></div>' +
      '<div class="sk-empty">No tool calls in this window. Widen the time filter, or run <code>tuneloop analyze</code> to ingest more sessions.</div></div>';
    return;
  }

  if (state.tool) { paintToolPage(box, state.tool); return; }
  paintRoster(box, d);
}

function kindSeg() {
  return '<div class="seg seg-primary th-kinds">' + KINDS.map(function (k) {
    var on = k.k === state.toolKind;
    return '<button type="button"' + (on ? ' class="on"' : '') + ' data-k="' + k.k + '"' +
      ' aria-pressed="' + (on ? 'true' : 'false') + '" title="' + esc(k.full) + '">' +
      esc(k.l) + '</button>';
  }).join('') + '</div>';
}

function paintRoster(box, d) {
  box.innerHTML =
    '<div class="panel sk-panel">' +
      '<div class="filter-bar sk-scope" id="th-scope"></div>' +
      kindSeg() +
      '<div class="sk-overview" id="th-overview"></div>' +
      '<div class="th-overall" id="th-overall"></div>' +
      '<div class="filters sk-filters" id="th-filters"></div>' +
      '<div class="sk-roster" id="th-roster"></div>' +
      '<div class="th-roster-note" id="th-roster-note"></div>' +
    '</div>';

  if (hasScopeControls()) {
    mountScopeBar(document.getElementById('th-scope'), function () {
      tlReport = null; tlReportKey = null; loadTools();
    });
  }

  Array.prototype.forEach.call(box.querySelectorAll('.th-kinds button'), function (b) {
    b.onclick = function () { openTool(this.getAttribute('data-k'), null); };
  });

  renderTimeBar(d);
  renderOverview(d);
  renderOverallTrend(d);
  renderFilters(d);
  renderRoster(section(d).rows || []);
  renderRosterNote(d);
}

function renderOverview(d) {
  var bar = $('#th-overview');
  if (!bar) return;
  var s = section(d);
  var mcp = state.toolKind === 'mcp';
  var stats = mcp
    ? [{ k: '', v: s.rows.length, l: 'Servers', tip: 'MCP servers called in this window.' }]
    : [{ k: '', v: s.rows.length, l: 'Tools', tip: 'Built-in tools and shell binaries called in this window.' }];
  stats.push({ k: 'high-error', v: s.totalHighError, l: 'High error', tip: FLAGS['high-error'].tip });
  if (s.totalDegrading > 0) stats.push({ k: 'degrading', v: s.totalDegrading, l: 'Degrading', tip: FLAGS.degrading.tip });

  bar.innerHTML = stats.map(function (x) {
    var on = x.k === tlStatus;
    var hero = x.k === 'high-error' || x.k === 'degrading';
    return '<button type="button" class="sk-stat' + (on ? ' on' : '') + (hero && x.v > 0 ? ' th-stat-hero' : '') +
      '" data-k="' + esc(x.k) + '" title="' + esc(x.tip) + '">' +
      '<span class="sk-stat-v">' + num(x.v || 0) + '</span>' +
      '<span class="sk-stat-l">' + esc(x.l) + '</span>' +
      '</button>';
  }).join('');

  Array.prototype.forEach.call(bar.querySelectorAll('.sk-stat'), function (el) {
    el.onclick = function () { tlStatus = this.getAttribute('data-k'); paintTools(); };
  });
}

function renderOverallTrend(d) {
  var host = $('#th-overall');
  if (!host) return;
  var kind = KINDS.filter(function (k) { return k.k === state.toolKind; })[0];
  var label = kind ? kind.noun : 'tool';
  var t = (section(d) || {}).trend;
  if (!t || !t.calls) { host.innerHTML = ''; return; }

  var span = esc(winPhrase('in the last ', 'across your full history'));
  var rate = pct(t.errors / t.calls);
  if (!t.chartable) {
    host.innerHTML =
      '<div class="sk-card th-overall-card">' +
        sectHead('Error rate over time', 'Too little ' + label + ' activity to plot a rate per ' + trendGranLabel(d.sparkBuckets) + ', so the totals are stated instead.') +
        '<div class="sk-empty th-thin">Too few ' + esc(label) + ' calls to chart a rate. ' +
          num(t.errors) + ' of ' + num(t.calls) + ' failed ' + span +
          ', across ' + num(t.activeDays) + (t.activeDays === 1 ? ' day' : ' days') + ' with any activity.</div>' +
      '</div>';
    return;
  }

  host.innerHTML =
    '<div class="sk-card th-overall-card">' +
      sectHead('Error rate over time', 'Every ' + label + ' call, bucketed by when the call ran. Each bar is that bucket\'s failed calls over its total calls. Calls count once each, so this is a true rate and won\'t equal the sum of the rows below.') +
      '<div class="sk-trend" id="th-overall-trend">' + rateChart(t.errorSpark, t.callSpark, d.sparkBuckets) + '</div>' +
      '<div class="sk-sect-note">' + num(t.errors) + ' of ' + num(t.calls) + ' ' + esc(label) + ' calls failed ' +
        span + ' (' + rate + ' overall), per ' +
        trendGranLabel(d.sparkBuckets) + '. Hover a bar for its exact split.</div>' +
    '</div>';
  wireTrendTooltip($('#th-overall-trend'));
}

function renderTimeBar(d) {
  var bar = $('#th-scope');
  if (!bar) return;

  var segBtns = TIME_PRESETS.map(function (p) {
    return '<button type="button" data-d="' + p.d + '"' +
      (String(p.d) === String(state.toolWin) ? ' class="on"' : '') + '>' + p.l + '</button>';
  }).join('');

  var sources = (d && d.availableSources) || [];
  var sourceGrp = '';
  if (sources.length > 1) {
    var sourceOpts = sources.map(function (s) {
      return '<option value="' + esc(s) + '"' + (s === d.source ? ' selected' : '') + '>' + esc(sourceLabel(s)) + '</option>';
    }).join('');
    sourceGrp = '<span class="fb-grp"><span class="fb-lbl">Agent</span><select class="fb-sel" id="th-source">' + sourceOpts + '</select></span>';
  }

  var timeHtml =
    '<span class="fb-grp"><span class="fb-lbl">Time</span>' +
    '<div class="seg flt-seg" id="th-time">' + segBtns + '</div></span>' +
    '<span class="flt-dates" id="th-dates"' + (state.toolWin === 'custom' ? '' : ' hidden') + '>' +
      '<input type="date" id="th-from" value="' + esc(state.toolFrom) + '" />' +
      '<span class="flt-dash">→</span>' +
      '<input type="date" id="th-to" value="' + esc(state.toolTo) + '" />' +
    '</span>' +
    sourceGrp;

  bar.insertAdjacentHTML('beforeend', timeHtml);

  Array.prototype.forEach.call(bar.querySelectorAll('#th-time button'), function (b) {
    b.onclick = function () {
      var v = this.getAttribute('data-d');
      setToolWin(v === 'all' || v === 'custom' ? v : parseInt(v, 10));
    };
  });
  var from = $('#th-from'), to = $('#th-to');
  if (from) from.onchange = function () { state.toolFrom = this.value; setToolWin('custom'); };
  if (to) to.onchange = function () { state.toolTo = this.value; setToolWin('custom'); };
  var srcSel = $('#th-source');
  if (srcSel) srcSel.onchange = function () { setToolSource(this.value); };
}

function renderFilters(d) {
  var bar = $('#th-filters');
  if (!bar) return;
  var mcp = state.toolKind === 'mcp';

  var opts = mcp
    ? [['', 'All servers'], ['high-error', 'High error'], ['degrading', 'Degrading']]
    : [['', 'All tools'], ['high-error', 'High error'], ['degrading', 'Degrading']];
  var statusOpts = opts.map(function (o) {
    return '<option value="' + esc(o[0]) + '"' + (o[0] === tlStatus ? ' selected' : '') + '>' + esc(o[1]) + '</option>';
  }).join('');

  bar.innerHTML =
    '<div class="flt-row">' +
      '<input id="th-search" class="flt-search" placeholder="' + (mcp ? 'search server name' : 'search tool or binary') + '" value="' + esc(tlSearch) + '" />' +
      '<span class="flt-grp"><span class="flt-lbl">Status</span><select id="th-status">' + statusOpts + '</select></span>' +
    '</div>';

  $('#th-status').onchange = function () { tlStatus = this.value; paintTools(); };

  var t;
  $('#th-search').oninput = function () {
    var v = this.value;
    clearTimeout(t);
    t = setTimeout(function () { tlSearch = v; renderRoster(section(tlReport).rows || []); }, 150);
  };
}

function renderRoster(rows) {
  var host = $('#th-roster');
  if (!host) return;
  var shown = filterRows(rows, state.toolKind, tlStatus, tlSearch);
  if (!shown.length) {
    var msg = tlStatus === '' && !tlSearch
      ? (state.toolKind === 'mcp'
          ? 'No MCP servers were called in this window.'
          : 'No built-in tools were called in this window.')
      : 'Nothing matches this view.';
    host.innerHTML = '<div class="sk-empty">' + msg + '</div>';
    return;
  }
  var tail = [];
  if (state.toolKind === 'builtin' && !tlSearch && !tlStatus && !tlShowAllShell) {
    tail = rollupTail(shown);
    if (tail.length) {
      var rolled = {};
      tail.forEach(function (r) { rolled[r.name] = 1; });
      shown = shown.filter(function (r) { return !rolled[r.name]; });
    }
  }

  host.innerHTML = shown.map(toolRow).join('') + (tail.length ? rollupRow(tail) : '') +
    (tlShowAllShell ? '<div class="th-rollup-back"><a id="th-collapse">collapse the shell long tail</a></div>' : '');

  Array.prototype.forEach.call(host.querySelectorAll('.sk-row-head'), function (el) {
    el.onclick = function () { openTool(state.toolKind, this.getAttribute('data-name')); };
  });
  var roll = host.querySelector('#th-rollup');
  if (roll) roll.onclick = function () { tlShowAllShell = true; paintTools(); };
  var collapse = host.querySelector('#th-collapse');
  if (collapse) collapse.onclick = function () { tlShowAllShell = false; paintTools(); };
}

function rollupRow(tail) {
  var calls = 0, errors = 0;
  tail.forEach(function (r) { calls += r.calls; errors += r.errorCalls; });
  var shownNames = tail.slice(0, 12).map(function (r) { return r.name; }).join(', ');
  var names = tail.length > 12 ? shownNames + ', +' + num(tail.length - 12) + ' more' : shownNames;
  return '<div class="sk-row">' +
    '<div class="sk-row-head th-rollup" id="th-rollup" title="' + esc(names) + '">' +
      '<span class="sk-dot th-dot-none"></span>' +
      '<span class="sk-name th-rollup-name">' + num(tail.length) + ' more shell binaries</span>' +
      '<span class="sk-flags"></span>' +
      '<span class="sk-spark"></span>' +
      '<span class="sk-metric"><span class="sk-mv">' + num(calls) + '</span><span class="sk-ml">calls</span></span>' +
      '<span class="sk-metric"><span class="sk-mv">—</span><span class="sk-ml">sessions</span></span>' +
      '<span class="sk-metric"><span class="sk-mv">' + (calls ? pct(errors / calls) : '—') + '</span><span class="sk-ml">errors</span></span>' +
      '<span class="sk-caret">+</span>' +
    '</div>' +
    '</div>';
}

function renderRosterNote(d) {
  var host = $('#th-roster-note');
  if (!host) return;
  if (state.toolKind !== 'builtin') { host.innerHTML = ''; return; }
  var s = section(d);
  var note = 'Shell binaries are counted per call that <em>involved</em> them — <code>npm ci &amp;&amp; npm test | tee log</code> ' +
    'counts toward <code>npm</code> and <code>tee</code>, so these rows deliberately don\'t sum to your total shell calls.';
  if (s.unlabeledShellCalls > 0) {
    note += ' A further ' + num(s.unlabeledShellCalls) + ' shell ' + (s.unlabeledShellCalls === 1 ? 'call' : 'calls') +
      ' ran no binary of their own (<code>cd</code>, a bare redirect) and appear in no row.';
  }
  host.innerHTML = note;
}

function callsTip(r) {
  var base = r.shell
    ? num(r.calls) + ' shell ' + (r.calls === 1 ? 'call' : 'calls') + ' involved ' + r.name
    : num(r.calls) + ' ' + (r.calls === 1 ? 'call' : 'calls');
  return r.sidechainCalls > 0 ? base + ' · ' + num(r.sidechainCalls) + ' via subagents' : base;
}

function toolRow(r) {
  var chips = (r.flags || []).map(function (f) {
    var fl = FLAGS[f];
    return fl ? '<span class="sk-flag" style="color:' + fl.color + ';border-color:' + fl.color + '" title="' + esc(flagTip(r, f)) + '">' + esc(fl.label) + '</span>' : '';
  }).join('');
  var errRate = r.calls > 0 ? pct(r.errorCalls / r.calls) : '—';
  return '<div class="sk-row">' +
    '<div class="sk-row-head" data-name="' + esc(r.name) + '">' +
      '<span class="sk-dot th-dot-none"></span>' +
      '<span class="sk-name">' + esc(r.name) + '</span>' +
      (r.shell ? '<span class="th-kind-chip" title="A binary run through the shell, promoted out of Bash commands.">shell</span>' : '') +
      (r.repoLocal ? '<span class="th-kind-chip" title="Invoked by repo-relative path — this project\'s own script.">repo script</span>' : '') +
      '<span class="sk-flags">' + chips + '</span>' +
      '<span class="sk-spark">' + sparkline(r.spark) + '</span>' +
      '<span class="sk-metric" title="' + esc(callsTip(r)) + '">' +
        '<span class="sk-mv">' + num(r.calls) + '</span><span class="sk-ml">' + (r.shell ? 'involving' : 'calls') + '</span></span>' +
      '<span class="sk-metric"><span class="sk-mv">' + num(r.sessions) + '</span><span class="sk-ml">sessions</span></span>' +
      '<span class="sk-metric' + (hasFlag(r, 'high-error') ? ' th-metric-bad' : '') + '"><span class="sk-mv">' + errRate + '</span><span class="sk-ml">errors</span></span>' +
      '<span class="sk-caret">›</span>' +
    '</div>' +
    '</div>';
}

// ---- Per-entity detail page -------------------------------------------------

function backHead(kind) {
  return '<div class="sk-page-head"><button class="sk-back" id="th-back">← All ' +
    (kind === 'mcp' ? 'servers' : 'tools') + '</button></div>';
}

function paintToolPage(box, name) {
  var key = state.toolKind + ' ' + name + ' ' + (state.toolFilter || '');
  if (tlDetail && tlDetailKey === key + '|' + tlWinKey()) { renderToolPage(box, tlDetail); return; }
  box.innerHTML = backHead(state.toolKind) +
    '<div class="sk-empty">Loading ' + esc(name) + '…</div>';
  var back = box.querySelector('#th-back');
  if (back) back.onclick = function () { openTool(state.toolKind, null); };

  var sent = { kind: state.toolKind, name: name, filter: state.toolFilter || '', win: tlWinKey() };
  get('/api/tool-health/' + encodeURIComponent(sent.kind) + '/' + encodeURIComponent(name) +
      '?' + (sent.filter ? 'tool=' + encodeURIComponent(sent.filter) + '&' : '') + tlWinQuery())
    .then(function (d) {
      if (state.tool !== name) return;
      if (!detailStillCurrent(sent, { kind: state.toolKind, name: state.tool, filter: state.toolFilter, win: tlWinKey() })) return;
      if (!d || !d.row) {
        state.tool = null;
        syncHash({ replace: true });
        paintTools();
        return;
      }
      state.toolFilter = d.tool || '';
      tlDetail = d;
      tlDetailKey = sent.kind + ' ' + name + ' ' + (state.toolFilter || '') + '|' + sent.win;
      renderToolPage($('#tool-health'), d);
    })
    .catch(function () {
      var b = $('#tool-health');
      if (b) b.innerHTML = '<div class="sk-empty">Could not load this tool.</div>';
    });
}

function renderToolPage(box, d) {
  if (!box) return;
  var r = d.row;
  var mcp = d.kind === 'mcp';
  var chips = (r.flags || []).map(function (f) {
    var fl = FLAGS[f];
    return fl ? '<span class="sk-flag sk-flag-lg" style="color:' + fl.color + ';border-color:' + fl.color + '" title="' + esc(flagTip(r, f)) + '">' + esc(fl.label) + '</span>' : '';
  }).join('');
  var accent = hasFlag(r, 'high-error') ? 'var(--red)' : hasFlag(r, 'degrading') ? 'var(--amber)' : 'var(--emerald)';

  var html = backHead(d.kind);
  html += '<div class="sk-page-title">' +
    '<h2 class="sk-page-name">' + esc(r.name) + '</h2>' +
    (r.shell ? '<span class="th-kind-chip">shell</span>' : '') +
    (r.repoLocal ? '<span class="th-kind-chip" title="Invoked by repo-relative path — this project\'s own script.">repo script</span>' : '') +
    chips +
    '</div>';

  html += toolChips(d);

  if (d.tool) {
    html += '<div class="th-scoped">Every number below is <strong>' + esc(toolLabelOf(d, d.tool)) +
      '</strong> only, not all of ' + esc(r.name) + '.</div>';
  }

  var winSub = winPhrase('in the last ', 'over all time');
  var callsSub = (r.shell ? 'shell calls involving ' + r.name + ', ' : '') + winSub;
  if (r.sidechainCalls > 0) callsSub += ' · ' + num(r.sidechainCalls) + ' via subagents';
  html += '<div class="sk-page-tiles">' +
    pageTile(num(r.calls), r.shell ? 'Calls involving it' : 'Calls', callsSub) +
    pageTile(num(r.sessions), 'Sessions', 'distinct sessions it ran in') +
    pageTile(r.calls > 0 ? pct(r.errorCalls / r.calls) : '—', 'Error rate',
      r.calls > 0 ? num(r.errorCalls) + ' of ' + num(r.calls) + ' calls failed' : 'no calls to measure') +
    pageTile(r.emptyEligibleCalls > 0 ? pct(r.emptyCalls / r.emptyEligibleCalls) : '—', 'Empty results',
      r.emptyEligibleCalls > 0
        ? num(r.emptyCalls) + ' of ' + num(r.emptyEligibleCalls) +
          (r.emptyEligibleCalls === r.calls ? ' successful calls returned nothing' : ' retrieval calls returned nothing')
        : mcp
          ? 'no retrieval calls — this server\'s tools all write, or none succeeded'
          : 'nothing retrieved here — empty output means success') +
    '</div>';

  html += '<div class="sk-advice" style="border-left-color:' + accent + '">' + esc(d.advice) + '</div>';

  html += '<div class="sk-card th-fix-card" id="th-fix" style="display:none"></div>';

  if (r.calls > 0) {
    html += '<div class="sk-card">' +
      sectHead('Error rate over time', 'Each bar is that bucket\'s failed calls over its total calls, dated by when the call ran.') +
      '<div class="sk-trend" id="th-trend">' + rateChart(r.errorSpark, r.spark, d.sparkBuckets) + '</div>' +
      '<div class="sk-sect-note">Per ' + trendGranLabel(d.sparkBuckets) + ', ' +
        esc(winPhrase('over the last ', 'across your full history')) + '. Hover a bar for the calls behind it.</div>' +
      '</div>';
  }

  if (d.errorCategories && d.errorCategories.length) {
    html += '<div class="sk-card">' +
      sectHead('Errors by category', 'What its failures were, fingerprinted into a shared taxonomy. Open one to see the actual failed calls and step through them in the transcripts.') +
      '<div class="errcat" id="th-errcat"></div>' +
      '</div>';
  }

  if (d.perRepo && d.perRepo.length) {
    html += '<div class="sk-card">' +
      '<div class="sk-sect-h">Where it\'s used</div>' + repoBreakdown(d.perRepo, accent) + '</div>';
  }

  html += '<div class="sk-card"><div class="sk-sect-h">Details</div><div class="sk-facts">';
  if (mcp) {
    html += fact('Install scope', r.installed ? (d.details.scope === 'global' ? 'Global' : 'Project') : 'Not installed (seen running)');
    if (d.details.sourceFiles && d.details.sourceFiles.length) html += fact('Declared in', d.details.sourceFiles.join(', '));
    if (d.details.installedRepos && d.details.installedRepos.length) html += fact('Installed in', d.details.installedRepos.join(', '));
    if (d.details.type) html += fact('Transport', d.details.type);
    if (d.details.url) html += fact('URL', d.details.url);
  } else {
    html += fact('Kind', r.shell ? 'Shell binary' : 'Built-in tool');
  }
  if (r.calls > 0) {
    html += fact('First used', r.firstUsedAt ? String(r.firstUsedAt).slice(0, 10) : '—');
    html += fact('Last used', r.lastUsedAt ? String(r.lastUsedAt).slice(0, 10) : '—');
  }
  html += '</div></div>';

  if (d.sessions && d.sessions.length) {
    var listed = d.sessions.reduce(function (a, g) { return a + g.calls; }, 0);
    var note = 'Sessions where it failed come first, then most recent — and the same inside each ' +
      'session. Click a session to see its calls; each one opens the transcript at that call.';
    if (d.sessions.length < r.sessions) {
      note += ' Showing ' + num(d.sessions.length) + ' of ' + num(r.sessions) + ' sessions (' +
        num(listed) + ' of ' + num(r.calls) + ' calls).';
    }
    html += '<div class="sk-card">' +
      '<div class="sk-sect-h">Calls</div>' +
      '<div class="sk-sect-note">' + esc(note) + '</div>' +
      '<div class="th-calls" id="th-calls">' + d.sessions.map(sessionGroupRow).join('') + '</div>' +
      '</div>';
  }

  box.innerHTML = html;

  var back = box.querySelector('#th-back');
  if (back) back.onclick = function () { openTool(state.toolKind, null); };
  if (r.calls > 0) wireTrendTooltip(box.querySelector('#th-trend'));
  if (!d.tool && hasFlag(r, 'high-error')) loadFixCard(d);
  wireCallGroups(box);
  if (d.errorCategories && d.errorCategories.length) renderErrorCats(box, d);

  Array.prototype.forEach.call(box.querySelectorAll('.th-chip'), function (el) {
    el.onclick = function () { setToolFilter(this.getAttribute('data-raw')); };
  });

  if (keepScroll != null) {
    var y = keepScroll;
    keepScroll = null;
    requestAnimationFrame(function () { window.scrollTo(0, y); });
  }
}

// ---- Fix card (admin auto-load vs on-demand "Investigate & Suggest Fix") ----

function isAdminOrgWide(): boolean {
  if (!state.me || state.me.role !== 'admin') return false;
  var s = state.scope;
  return !s.team && (!s.repos || !s.repos.length) && !s.user;
}

function loadFixCard(d) {
  var host = $('#th-fix');
  if (!host) return;

  if (isAdminOrgWide()) {
    loadCachedFixCard(d, host);
  } else {
    showInvestigateButton(d, host);
  }
}

function loadCachedFixCard(d, host) {
  var sent = { kind: d.kind, name: d.name, filter: '', win: tlWinKey() };
  get('/api/tool-health/' + encodeURIComponent(d.kind) + '/' + encodeURIComponent(d.name) +
      '/advice?' + tlWinQuery())
    .then(function (a) {
      if (!a || state.tool !== d.name) return;
      if (!detailStillCurrent(sent, { kind: state.toolKind, name: state.tool, filter: state.toolFilter, win: tlWinKey() })) return;
      renderFixCardContent(host, a, d);
    })
    .catch(function () { /* leave hidden */ });
}

function showInvestigateButton(d, host) {
  host.innerHTML =
    sectHead('Suggested fix', 'Draft a fix recommendation by reading the full error text of these failures with an LLM.') +
    '<button type="button" class="ins-btn th-fix-investigate" id="th-fix-btn">Investigate &amp; Suggest Fix</button>';
  host.style.display = '';
  var btn = document.getElementById('th-fix-btn');
  if (btn) btn.onclick = function () { triggerOnDemandFix(d, host); };
}

function triggerOnDemandFix(d, host) {
  host.innerHTML =
    sectHead('Suggested fix') +
    '<div class="sk-empty">Analyzing error samples…</div>';
  post('/api/tool-health/' + encodeURIComponent(d.kind) + '/' + encodeURIComponent(d.name) +
      '/advice?' + tlWinQuery(), {})
    .then(function (a) {
      if (!a || state.tool !== d.name) return;
      renderFixCardContent(host, a, d);
    })
    .catch(function (e) {
      host.innerHTML =
        sectHead('Suggested fix') +
        '<div class="sk-empty">Could not generate a fix. ' +
          (e && e.message ? esc(e.message) : 'Check that an LLM provider is configured in Settings.') + '</div>';
    });
}

function renderFixCardContent(host, a, d) {
  var stamp = a.generatedAt ? ' · drafted ' + dayOf(a.generatedAt) : '';
  host.innerHTML =
    sectHead('Suggested fix', 'Written by reading the full error text of these failures — not the 200-character summaries stored for the roster. Check it before pasting: it is a draft from the evidence, not a verified fix.') +
    (a.diagnosis ? '<div class="th-fix-diag">' + esc(a.diagnosis) + '</div>' : '') +
    (a.snippet
      ? '<div class="th-fix-snip"><div class="th-fix-snip-head">' +
          '<span>Paste into your agent instructions (CLAUDE.md or equivalent)</span>' +
          '<button type="button" class="ins-btn th-fix-copy">Copy</button></div>' +
          '<pre class="th-fix-pre">' + esc(a.snippet) + '</pre></div>'
      : '<div class="sk-sect-note">No instruction would prevent these — see the diagnosis.</div>') +
    '<div class="sk-sect-note">' + esc((a.model || 'LLM') + stamp) +
      (a.stale ? ' <button type="button" class="ins-btn th-fix-refresh" id="th-fix-refresh">Refresh</button>' : '') +
    '</div>';
  host.style.display = '';
  var copy = host.querySelector('.th-fix-copy');
  if (copy) copy.onclick = function () { copySnippet(a.snippet, this); };
  var refresh = host.querySelector('#th-fix-refresh');
  if (refresh) refresh.onclick = function () { triggerOnDemandFix(d, host); };
}

function copySnippet(text, btn) {
  navigator.clipboard.writeText(text).then(function () {
    var orig = btn.textContent;
    btn.textContent = 'Copied ✓';
    setTimeout(function () { btn.textContent = orig; }, 1500);
  }, function () {
    var orig = btn.textContent;
    btn.textContent = 'Copy failed';
    setTimeout(function () { btn.textContent = orig; }, 1500);
  });
}

function toolChips(d) {
  var rows = (d.perTool || []).filter(function (t) { return !!t.raw; });
  if (!rows.length) return '';
  var all = !d.tool;
  return '<div class="th-chips" role="group" aria-label="Show one tool only">' +
    '<button type="button" class="th-chip th-chip-all' + (all ? ' on' : '') + '" data-raw=""' +
    ' aria-pressed="' + (all ? 'true' : 'false') + '" title="Show every tool on this server">All</button>' +
    rows.map(function (t) {
      var on = isToolSelected(t.raw, d.tool);
      return '<button type="button" class="th-chip' + (on ? ' on' : '') + '" data-raw="' + esc(t.raw) + '"' +
        ' aria-pressed="' + (on ? 'true' : 'false') + '" title="' + esc(chipTip(t, on)) + '">' +
        esc(t.name) + '<span class="th-chip-n">' + num(t.calls) + '</span>' +
        (t.errorCalls > 0 ? '<span class="th-chip-e">' + num(t.errorCalls) + ' err</span>' : '') +
        '</button>';
    }).join('') +
    '<span class="sk-info th-chips-info" title="' + esc('Tools this agent actually called, and the page\'s filter — pick one to narrow every number below to it. Observed calls only: there is no inventory of a server\'s full tool list, so a tool never called simply isn\'t here. Hover a tool for its errors and when it was last used.') + '">?</span>' +
    '</div>';
}

function chipTip(t, on) {
  return (on ? 'Showing ' + t.name + ' only — pick All to clear' : 'Show only ' + t.name) +
    '\n' + num(t.calls) + (t.calls === 1 ? ' call' : ' calls') +
    ', ' + num(t.errorCalls) + (t.errorCalls === 1 ? ' error' : ' errors') +
    ', last used ' + (t.lastUsedAt ? String(t.lastUsedAt).slice(0, 10) : 'never');
}

function toolLabelOf(d, raw) {
  var hit = (d.perTool || []).filter(function (t) { return t.raw === raw; })[0];
  return hit ? hit.name : raw;
}

var keepScroll = null;

function setToolFilter(raw) {
  keepScroll = window.scrollY;
  state.toolFilter = raw || '';
  syncHash();
  paintTools();
}

function repoBreakdown(rows, color) {
  var max = 0;
  for (var i = 0; i < rows.length; i++) if (rows[i].calls > max) max = rows[i].calls;
  if (max <= 0) max = 1;
  return '<div class="sk-repos">' + rows.map(function (p) {
    var label = p.repo == null ? 'unattributed' : p.repo;
    var w = Math.max(2, Math.round((p.calls / max) * 100));
    var sub = num(p.calls) + (p.calls === 1 ? ' call' : ' calls') + ' · ' + num(p.sessions) + (p.sessions === 1 ? ' session' : ' sessions');
    return '<div class="sk-repo' + (p.repo == null ? ' sk-repo-null' : '') + '">' +
      '<div class="sk-repo-name" title="' + esc(label) + '">' + esc(label) + '</div>' +
      '<div class="sk-repo-track"><div class="sk-repo-bar" style="width:' + w + '%;background:' + color + '"></div></div>' +
      '<div class="sk-repo-n">' + esc(sub) + '</div>' +
      '</div>';
  }).join('') + '</div>';
}

function sessionGroupRow(g) {
  var single = g.calls === 1 && g.items.length === 1;
  var when = g.lastTs ? String(g.lastTs).slice(0, 10) : '—';
  var errs = g.errorCalls > 0
    ? '<span class="th-grp-errs" title="' + esc(num(g.errorCalls) + ' of these failed') + '">' + num(g.errorCalls) + ' errored</span>'
    : '';
  return '<div class="th-grp" data-session="' + esc(g.sessionId) + '">' +
    '<button type="button" class="th-grp-head" data-single="' + (single ? '1' : '') + '"' +
      ' data-idx="' + esc(String(single ? g.items[0].idx : '')) + '">' +
      '<span class="th-grp-caret">' + (single ? '' : '›') + '</span>' +
      '<span class="th-grp-title">' + esc(g.title || g.sessionId) + '</span>' +
      (g.repo ? '<span class="sk-inv-repo">' + esc(g.repo) + '</span>' : '') +
      errs +
      '<span class="th-grp-n">' + num(g.calls) + (g.calls === 1 ? ' call' : ' calls') + '</span>' +
      '<span class="sk-inv-date">' + esc(when) + '</span>' +
      '<span class="sk-inv-go">' + (single ? 'open ↗' : '') + '</span>' +
    '</button>' +
    '<div class="th-grp-items" hidden>' + g.items.map(invocationRow).join('') +
      (g.items.length < g.calls
        ? '<div class="sk-sect-note th-grp-more">Showing ' + num(g.items.length) + ' of ' + num(g.calls) +
          ' — failures first, then the most recent of the rest.</div>'
        : '') +
    '</div>' +
    '</div>';
}

function wireCallGroups(box) {
  Array.prototype.forEach.call(box.querySelectorAll('.th-grp-head'), function (head) {
    head.onclick = function () {
      if (this.getAttribute('data-single')) {
        openDetail(this.parentNode.getAttribute('data-session'), { toolTarget: parseInt(this.getAttribute('data-idx'), 10) });
        return;
      }
      var items = this.parentNode.querySelector('.th-grp-items');
      var opening = items.hidden;
      items.hidden = !opening;
      this.classList.toggle('open', opening);
    };
  });
  Array.prototype.forEach.call(box.querySelectorAll('.sk-inv'), function (el) {
    el.onclick = function () {
      openDetail(this.getAttribute('data-session'), { toolTarget: parseInt(this.getAttribute('data-idx'), 10) });
    };
  });
}

function invocationRow(o) {
  var when = o.ts ? String(o.ts).slice(0, 10) : '—';
  var tags = '';
  if (o.sidechain) tags += '<span class="sk-inv-tag sk-inv-sub" title="Ran inside a subagent, not the main conversation.">subagent</span>';
  if (o.compound) tags += COMPOUND_BADGE;
  if (o.isError && o.blamedElsewhere) {
    tags += '<span class="sk-inv-tag th-compound" title="This call failed, but its output named a different binary in the same command.">failed elsewhere</span>';
  } else if (o.isError) {
    tags += '<span class="sk-inv-tag sk-inv-err">errored</span>';
  }
  var what = o.command || o.name || '';
  return '<button class="sk-inv" data-session="' + esc(o.sessionId) + '" data-idx="' + esc(String(o.idx)) + '">' +
    '<span class="sk-inv-title">' + esc(o.title || o.sessionId) + '</span>' +
    '<span class="th-inv-what" title="' + esc(what) + '">' + esc(clip(what, 52)) + '</span>' +
    '<span class="sk-inv-tags">' + tags + '</span>' +
    (o.repo ? '<span class="sk-inv-repo">' + esc(o.repo) + '</span>' : '') +
    '<span class="sk-inv-date">' + esc(when) + '</span>' +
    '<span class="sk-inv-go">open ↗</span>' +
    '</button>';
}

function clip(s, n) { s = String(s == null ? '' : s); return s.length > n ? s.slice(0, n - 1) + '…' : s; }

var COMPOUND_BADGE = '<span class="sk-inv-tag th-compound" title="This command ran several binaries and its output didn\'t say which one failed, so the failure is listed under each. Open the transcript to see which part broke.">compound</span>';

// ---- Errors by category ----------------------------------------------------

var catTips = null;

function renderErrorCats(box, d) {
  var host = box.querySelector('#th-errcat');
  if (!host) return;
  var rows = d.errorCategories;
  var total = rows.reduce(function (a, c) { return a + c.calls; }, 0);
  var max = rows[0] ? rows[0].calls : 1;

  var tipsP = catTips ? Promise.resolve(catTips) : get('/api/error-categories').then(function (cats) {
    catTips = {};
    (cats || []).forEach(function (c) { catTips[c.key] = c; });
    return catTips;
  }).catch(function () { return (catTips = {}); });

  tipsP.then(function (tips) {
    if (state.tool !== d.name) return;
    host.innerHTML = rows.map(function (row) {
      var meta = tips[row.category] || { label: row.category, description: '' };
      var w = max ? Math.round((row.calls / max) * 100) : 0;
      var share = total ? Math.round((row.calls / total) * 100) : 0;
      return '<div class="errcat-item" data-cat="' + esc(row.category) + '" data-total="' + row.calls + '">' +
        '<div class="bar-row errcat-row" data-cat="' + esc(row.category) + '">' +
          '<span class="name" title="' + esc(meta.description) + '">' + esc(meta.label) + '</span>' +
          '<span class="bar-track"><span class="bar-fill" style="width:' + w + '%"></span></span>' +
          '<span class="n"><span class="cnt">' + num(row.calls) + '</span><span class="pct">' + share + '%</span></span>' +
        '</div>' +
        '<div class="errcat-occ" hidden></div></div>';
    }).join('');
    Array.prototype.forEach.call(host.querySelectorAll('.errcat-row'), function (el) {
      el.onclick = function () { toggleOcc(host, el.parentNode, el.getAttribute('data-cat'), tips, d); };
    });
  });
}

function toggleOcc(host, item, cat, tips, d) {
  var panel = item.querySelector('.errcat-occ');
  if (!panel) return;
  var wasOpen = !panel.hidden;
  Array.prototype.forEach.call(host.querySelectorAll('.errcat-occ'), function (p) {
    p.hidden = true;
    p.parentNode.querySelector('.errcat-row').classList.remove('open');
  });
  if (wasOpen) return;
  panel.hidden = false;
  item.querySelector('.errcat-row').classList.add('open');
  if (panel.getAttribute('data-loaded')) return;
  panel.innerHTML = '<div class="occ-loading">Loading…</div>';
  var total = parseInt(item.getAttribute('data-total'), 10) || 0;
  get('/api/tool-health/' + encodeURIComponent(d.kind) + '/' + encodeURIComponent(d.name) +
      '/errors?category=' + encodeURIComponent(cat) +
      (d.tool ? '&tool=' + encodeURIComponent(d.tool) : '') + '&' + tlWinQuery())
    .then(function (occ) {
      panel.setAttribute('data-loaded', '1');
      renderOcc(panel, cat, occ || [], tips, total);
    })
    .catch(function () {
      panel.innerHTML = '<div class="occ-empty">Could not load occurrences.</div>';
    });
}

function renderOcc(panel, cat, occ, tips, total) {
  if (!occ.length) { panel.innerHTML = '<div class="occ-empty">No occurrences in this window.</div>'; return; }
  var label = (tips[cat] && tips[cat].label) || cat;
  var count = total || occ.length;
  var moreN = Math.max(0, count - occ.length);
  var list = occ.map(function (o, i) {
    var cmd = o.command || o.targetPath || '';
    return '<div class="occ-row" data-i="' + i + '" title="click to open the transcript at this error">' +
      '<span class="occ-tool">' + esc(o.name) + '</span>' +
      '<span class="occ-cmd" title="' + esc(cmd) + '">' +
        (o.binaryCount > 1 ? COMPOUND_BADGE : '') + esc(clip(cmd, 44)) + '</span>' +
      '<span class="occ-msg" title="' + esc(o.message || '') + '">' + esc(clip(o.message || '', 60)) + '</span>' +
      '<span class="occ-sess">' + esc(clip(o.title || '(untitled)', 22)) + '</span>' +
      '<span class="occ-date">' + esc(dayOf(o.ts || o.startedAt)) + '</span></div>';
  }).join('');
  panel.innerHTML = '<div class="occ-head">' + num(count) + ' occurrence' + (count > 1 ? 's' : '') + '</div>' +
    '<div class="occ-list">' + list + '</div>' +
    (moreN ? '<div class="occ-more">+ ' + num(moreN) + ' more (showing ' + occ.length + ')</div>' : '');
  Array.prototype.forEach.call(panel.querySelectorAll('.occ-row'), function (el) {
    el.onclick = function () { startErrorWalk(label, occ, parseInt(el.getAttribute('data-i'), 10)); };
  });
}
